import { useI18n } from '@ecom/i18n';
import { type ReactElement, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { NAV_ITEMS } from './nav';
import { NavIcon } from './nav-icons';
import { ShellControls } from './Topbar';

import { cn } from '@/lib/cn';
import { useSession } from '@/lib/session';

/**
 * A fixed column from `md` up; below it, a drawer from the start edge (the same side, so it flips under `dir="rtl"`)
 * over a scrim. Escape, the scrim and the close button all dismiss it.
 */
export function Sidebar({
  mobileOpen,
  onCloseMobile,
}: {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}): ReactElement {
  const { t } = useI18n();
  const { roles } = useSession();
  const closeRef = useRef<HTMLButtonElement>(null);
  const items = NAV_ITEMS.filter(
    (item) => item.onlyFor === undefined || roles.includes(item.onlyFor),
  );

  useEffect(() => {
    if (!mobileOpen) return undefined;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseMobile();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen, onCloseMobile]);

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t('merchant.topbar.menuClose')}
          onClick={onCloseMobile}
          className="fixed inset-0 z-40 bg-[rgb(8_16_34/0.54)] backdrop-blur-[3px] md:hidden"
        />
      ) : null}
      <aside
        id="admin-sidebar"
        className={cn(
          'adm-sidebar w-64 shrink-0 flex-col md:static md:flex md:w-64 md:shadow-none',
          mobileOpen
            ? 'fixed inset-y-0 start-0 z-50 flex w-[min(18rem,85vw)] shadow-popover'
            : 'hidden',
        )}
      >
        <div className="flex items-center gap-2 px-5 py-5">
          {/* The ABOUEB logo on its own white tile (the artwork has a white ground), cropped on the circle and the wordmark. */}
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-[0_6px_16px_rgb(0_0_0/0.18)]">
            <img
              src={`${import.meta.env.BASE_URL}icon-192.png`}
              alt=""
              className="h-full w-full object-contain"
            />
          </span>
          <div className="min-w-0 leading-tight">
            <p className="adm-sidebar__brand-title font-display text-base font-extrabold">
              {t('merchant.sidebar.brand')}
            </p>
            <p className="adm-sidebar__brand-sub text-xs">{t('admin.sidebar.subtitle')}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onCloseMobile}
            aria-label={t('merchant.topbar.menuClose')}
            className="adm-sidebar__close ms-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-md border md:hidden"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <nav
          className="flex-1 overflow-y-auto px-3 pb-4"
          aria-label={t('merchant.sidebar.navLabel')}
        >
          <ul className="flex flex-col gap-0.5">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onCloseMobile}
                  className="adm-nav-link flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors"
                  end
                >
                  <NavIcon to={item.to} />
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <ShellControls className="adm-sidebar__foot px-4 py-4 md:hidden" />
      </aside>
    </>
  );
}
